"use client";
import { cn } from "@/lib/utils";
import { Row } from "../row/Row";
import { Text } from "../text/Text";
import { Column } from "../column/Column";
import Logo from "@/components/logos/logo";
import { useEffect, useState } from "react";
import { withUser } from "@/hooks/with-user";
import { api } from "@/convex/_generated/api";
import html2canvas from "html2canvas-pro";
import { useMutation, useQuery } from "convex/react";
import Cal, { getCalApi } from "@calcom/embed-react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowRight, CalendarCheck, Check, CheckCircle, Loader2 } from "lucide-react";
import { uploadToCloudflareByFile } from "@/actions/cloudflare";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function extractCalLink(url: string): string {
  try {
    const parsed = new URL(url);
    // Strip leading slash: "/team/better-email-help" -> "team/better-email-help"
    return parsed.pathname.replace(/^\//, "");
  } catch {
    // Already a calLink path like "team/better-email-help"
    return url.replace(/^\//, "");
  }
}

export const SupportOutreach = ({
  showHeader = true,
  noPadding = false,
  row = false,
  className,
}: {
  showHeader?: boolean;
  noPadding?: boolean;
  row?: boolean;
  className?: string;
}) => {
  const { user } = withUser();
  const sendMessage = useMutation(api.slack.sendMessageMutation.sendMessageMutation);
  const bookingUrl = useQuery(api.systemSettings.queries.getSetting, { key: "supportBookingUrl" });

  const [sendingRequest, setSendingRequest] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  useEffect(() => {
    if (!showBooking) return;

    let cancelled = false;
    (async () => {
      const cal = await getCalApi();
      if (cancelled) return;
      cal("on", {
        action: "bookingSuccessfulV2",
        callback: () => {
          setIsBooked(true);
        },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [showBooking]);

  useEffect(() => {
    if (!isBooked) return;
    setTimeout(() => {
      setShowBooking(false);
    }, 2000);
  }, [isBooked]);

  const handleSupportOutreach = async () => {
    setSendingRequest(true);

    try {
      let result: { id: string; url: string } | null = null;
      try {
        const canvas = await html2canvas(document.body, {
          useCORS: true,
          allowTaint: true,
          logging: false,
          removeContainer: true,
        });
        const blob = await new Promise<Blob | null>((resolve) => { canvas.toBlob(resolve, "image/png"); });
        if (blob) {
          const file = new File([blob], `support-outreach-${user._id}-${Date.now()}.png`, { type: "image/png" });
          result = await uploadToCloudflareByFile(file, {
            type: "supportOutreach",
          });
        }
      } catch (error) {
        console.error("Failed to convert HTML to image", error);
      }

      let ip = "0";
      const ipResponse = await fetch("/api/ip");

      if (ipResponse.ok) {
        const ipData = (await ipResponse.json()) as { ip: string };
        ip = ipData.ip;
      }

      await sendMessage({
        channel: "alerts-customer-support",
        title: "🆘 Support Outreach",
        message: `${user.email} has requested support. IP: ${ip}`,
        userId: user._id,
        imageUrl: result?.url,
      });

      setRequestSent(true);
    } catch (error) {
      console.error("Failed to upload support outreach image", error);
    } finally {
      console.log("handleSupportOutreach finally");
      setSendingRequest(false);
    }
  };

  const calLink = bookingUrl ? extractCalLink(bookingUrl) : null;

  return (
    <Column>
      <motion.div
        key="support-outreach"
        className={cn("flex flex-col bg-white border border-border rounded-md shadow-xs", row && "flex-row", className)}
      >
        {showHeader && (
          <div className="flex flex-row flex-wrap border-b border-border px-4 py-2 bg-muted/50">
            <Row align between>
              <Row align tightGap className="">
                <Logo className="w-4 h-4" />
                <Text fat sm className="text-foreground ">
                  Better Email
                </Text>
              </Row>
              <Row align tightGap shrink>
                <Text sm className="text-muted-foreground">
                  Support staff on call
                </Text>
                <div className="w-2 h-2 bg-teal-500 rounded-full" />
              </Row>
            </Row>
          </div>
        )}

        {sendingRequest ? (
          <Column noGap className={cn("p-4", noPadding && "p-0")}>
            <Row align tightGap>
              <Loader2 className="h-5 w-5 text-teal-500 animate-spin" />
              <Text fat sm className="text-foreground ">
                Sending support request...
              </Text>
            </Row>
          </Column>
        ) : isBooked || requestSent ? (
          <Column noGap className={cn("p-4", noPadding && "p-0")}>
            <Row align tightGap>
              <CheckCircle className="h-5 w-5 text-teal-500" />
              <Text fat sm className="text-foreground ">
                Support request received! We will be in touch shortly.
              </Text>
            </Row>
          </Column>
        ) : (
          <Column
            tightGap
            className={cn("p-4", noPadding && "p-0", row && "flex-row w-full items-center p-0 gap-1")}
          >
            <Text sm subdued className={cn(row && "shrink-0")}>
              Questions?
            </Text>
            <div className="flex flex-row w-full">
              <Column noGap className={cn("gap-1", row && "flex flex-row items-center w-full")}>
                <Row align className={cn("gap-1", row && "w-fit flex-none gap-0")}>
                  {!row && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
                  <Text
                    sm
                    className="hover:underline cursor-pointer text-primary"
                    onClick={async () => { await handleSupportOutreach(); }}
                  >
                    Have us reach out to you
                  </Text>
                </Row>
                <Row align className={cn("gap-1", row && "w-fit flex-none lowercase")}>
                  {row ? (
                    <Text sm subdued>
                      or
                    </Text>
                  ) : (
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Text sm className="hover:underline cursor-pointer text-primary" onClick={() => { setShowBooking(true); }}>
                    Book a call
                  </Text>
                </Row>
              </Column>
            </div>
          </Column>
        )}
      </motion.div>

      <Dialog
        open={showBooking}
        onOpenChange={(open) => {
          setShowBooking(open);
          if (!open) setIsBooked(false);
        }}
      >
        <DialogContent className="min-w-0 max-w-[90%] w-[90vw] md:w-[90vw] h-[95vh] md:h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b border-border shrink-0">
            <DialogTitle>
              <Row align tightGap>
                <CalendarCheck className="h-5 w-5" />
                Book a Support Call
              </Row>
            </DialogTitle>
          </DialogHeader>
          <AnimatePresence mode="wait">
            {isBooked ? (
              <motion.div
                key="booked"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex-1 flex flex-col items-center justify-center gap-4 p-8"
              >
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <Text xl fat>
                  You're booked!
                </Text>
                <Text muted className="text-center">
                  We'll send you a confirmation email with the meeting details.
                </Text>
              </motion.div>
            ) : calLink ? (
              <motion.div key="cal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-0 flex-1">
                <Cal
                  calLink={calLink}
                  config={{
                    layout: "month_view",
                    theme: "light",
                  }}
                  style={{ width: "100%", height: "100%", overflow: "auto" }}
                />
              </motion.div>
            ) : (
              <Column className="flex-1 items-center justify-center">
                <Text muted>No booking URL configured.</Text>
              </Column>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </Column>
  );
};
